import { PlayIconFilledIcon } from 'assets/icons';
import { AlbumContentType, NotificationType } from 'common/enums';
import { HtmlRenderer } from 'common/html-renderer';
import { Spinner } from 'common/spinner';
import { appActions, AppContext } from 'context';
import { adaptContentType } from 'helpers/utils';
import { NotificationObject } from 'models/notificationObject';
import { AlbumContentSerialized } from 'networking/types/album-detail';
import { lazy, Suspense, useContext, useEffect, useRef } from 'react';
import { AudioPlayer } from './components/audio-player';
import styles from './render-content.module.scss';

const PdfViewer = lazy(() =>
  import('common/pdf-viewer').then((module) => ({ default: module.PdfViewer })),
);

export const RenderContent = ({
  content,
  isFullScreen,
  isPlaying,
  setIsPlaying,
  onFinish,
}: {
  content: AlbumContentSerialized;
  isFullScreen: boolean;
  isPlaying: boolean;
  // Lets the media itself (play overlay, native controls) drive the state.
  setIsPlaying?: (playing: boolean) => void;
  onFinish?: () => void;
}) => {
  const videoRef = useRef<HTMLVideoElement>(null);
  const { dispatch } = useContext(AppContext);

  // Keep `isPlaying` in step with the native player: the user can also start
  // or stop the video by clicking it or its timeline, and the overlay should
  // follow what the video is actually doing.
  useEffect(() => {
    const video = videoRef.current;
    if (!video || !setIsPlaying) return undefined;
    const onPlay = () => setIsPlaying(true);
    const onPause = () => {
      if (!video.ended) setIsPlaying(false);
    };
    video.addEventListener('play', onPlay);
    video.addEventListener('pause', onPause);
    return () => {
      video.removeEventListener('play', onPlay);
      video.removeEventListener('pause', onPause);
    };
  }, [content, setIsPlaying]);

  useEffect(() => {
    const video = videoRef.current;
    if (!video || adaptContentType(content) !== AlbumContentType.video) {
      return undefined;
    }

    const handlePlay = async () => {
      try {
        if (isPlaying) {
          await video.play();
        } else {
          video.pause();
        }
      } catch {
        // The browser refused to play (blocked autoplay, bad source): fall
        // back to the paused state so the play overlay comes back.
        setIsPlaying?.(false);
        dispatch({
          type: appActions.NOTIFICATION,
          notification: new NotificationObject({
            show: true,
            type: NotificationType.Error,
            message: 'Video playback failed. Please try again.',
          }),
        });
      }
    };

    handlePlay();
    const handleEnded = () => {
      if (onFinish) {
        onFinish();
      } else {
        video.currentTime = 0;
        handlePlay();
      }
    };

    video.addEventListener('ended', handleEnded);
    return () => {
      video.removeEventListener('ended', handleEnded);
    };
  }, [isPlaying, content, onFinish, setIsPlaying]);

  switch (adaptContentType(content)) {
    case AlbumContentType.video:
      return (
        <div className={styles.videoFrame}>
          <video
            ref={videoRef}
            src={content.mediaUrl}
            className={styles.mediaContent}
            poster={content.thumbnailUrl}
            // iPhones otherwise take a playing video into their own player.
            playsInline
            disablePictureInPicture
            controlsList="noplaybackrate"
            controls={!isFullScreen}
          >
            <track kind="captions" srcLang="en" label="English captions" />
          </video>
          {!isPlaying && (
            <button
              type="button"
              className={styles.playOverlay}
              onClick={() => setIsPlaying?.(true)}
              aria-label="Play video"
            >
              <PlayIconFilledIcon />
            </button>
          )}
        </div>
      );
    case AlbumContentType.audio:
      return (
        <AudioPlayer
          audioUrl={content.mediaUrl}
          isPlaying={isPlaying}
          onTogglePlay={setIsPlaying && (() => setIsPlaying(!isPlaying))}
          onFinish={onFinish}
          onEnded={() => setIsPlaying?.(false)}
          isFullScreen={isFullScreen}
        />
      );
    case AlbumContentType.image:
      return (
        <img
          src={content.mediaUrl}
          alt={content.name}
          className={styles.mediaContent}
        />
      );
    case AlbumContentType.document:
      return (
        <div className={styles.documentContent}>
          <Suspense fallback={<Spinner />}>
            {/* Keyed so each PDF opens on its first page. */}
            <PdfViewer
              key={content.id}
              url={content.mediaUrl}
              name={content.name}
              navigation="arrows"
            />
          </Suspense>
        </div>
      );
    case AlbumContentType.write:
      // TODO: Unify the logic for displaying content in view mode and content entry, to not re-write again the same
      return (
        <HtmlRenderer className={styles.text} html={content.content.body} />
      );
    default:
      return (
        <div>
          <p>Unsupported content type</p>
        </div>
      );
  }
};
